"use client"

import type React from "react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import type { RootState } from "../store/store"
import LoadingSpinner from "./common/LoadingSpinner"
import { useEffect, useState } from "react"
import { auth } from "../firebase/config"
import { isAdminUser } from "../services/auth"
import toast from "react-hot-toast"

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAuth?: boolean
  requireAdmin?: boolean
  redirectTo?: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
  redirectTo = "/login",
}) => {
  const { user, loading } = useSelector((state: RootState) => state.auth)
  const location = useLocation()
  const navigate = useNavigate()
  const [adminCheck, setAdminCheck] = useState({ loading: false, isAdmin: false, error: null as string | null })

  useEffect(() => {
    const checkAdmin = async () => {
      if (requireAdmin && user) {
        setAdminCheck({ loading: true, isAdmin: false, error: null })
        try {
          const isAdmin = user.role === "admin" || isAdminUser(user.email || "")
          setAdminCheck({ loading: false, isAdmin, error: null })
        } catch {
          setAdminCheck({ loading: false, isAdmin: false, error: "Failed to verify admin status." })
        }
      } else if (!requireAdmin) {
        setAdminCheck({ loading: false, isAdmin: true, error: null })
      } else {
        setAdminCheck({ loading: false, isAdmin: false, error: null })
      }
    }
    checkAdmin()
  }, [requireAdmin, user])

  useEffect(() => {
    if (user && auth.currentUser) {
      const isAdmin = user.role === "admin" || isAdminUser(user.email || "")
      const isEmailVerified = auth.currentUser.emailVerified || isAdmin

      if (!isEmailVerified && location.pathname !== "/") {
        toast.error("Please verify your email to access this page.")
        navigate("/", { replace: true })
      }
    }
  }, [user, location, navigate])

  if (loading || adminCheck.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (requireAuth && !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  if (user && auth.currentUser) {
    const isAdmin = user.role === "admin" || isAdminUser(user.email || "")
    const isEmailVerified = auth.currentUser.emailVerified || isAdmin

    if (!isEmailVerified && location.pathname === "/") {
      return (
        <div className="min-h-screen flex items-center justify-center text-red-600 font-bold">
          Please verify your email to access the rest of the site.
        </div>
      )
    }
  }

  if (requireAdmin) {
    const quickAdminCheck = user && (user.role === "admin" || isAdminUser(user.email || ""))

    if (adminCheck.error) {
      return <div className="min-h-screen flex items-center justify-center text-red-600 font-bold">{adminCheck.error}</div>
    }

    if (!(adminCheck.isAdmin || quickAdminCheck)) {
      return <Navigate to="/" replace />
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
